<script setup lang="ts">
import { computed, onMounted, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import trophyArt from '@/assets/gamification/celebration-trophies.png'
import ConfettiBurst from '@/components/gamification/ConfettiBurst.vue'
import AppIcon from '@/components/ui/AppIcon.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import { useRewardsStore, type RewardKind } from '@/stores/rewards'

const { t } = useI18n()
const rewards = useRewardsStore()

const reward = computed(() => rewards.current)

const KIND_LABEL: Record<RewardKind, string> = {
  lesson: 'rewards.kindLesson',
  quiz: 'rewards.kindQuiz',
  course: 'rewards.kindCourse',
  certificate: 'rewards.kindCertificate',
  journey: 'rewards.kindJourney',
}

function onKeydown(keyboardEvent: KeyboardEvent) {
  if (keyboardEvent.key === 'Escape' && rewards.current) {
    keyboardEvent.preventDefault()
    rewards.dismiss()
  }
}

watch(
  () => rewards.current,
  (current) => {
    if (current) void rewards.refreshPoints()
  },
)

onMounted(() => document.addEventListener('keydown', onKeydown))
onUnmounted(() => document.removeEventListener('keydown', onKeydown))
</script>

<template>
  <Teleport to="body">
    <div
      v-if="reward"
      class="fixed inset-0 z-50 flex items-center justify-center bg-stone-950/55 p-4"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="`reward-title-${reward.id}`"
      @click.self="rewards.dismiss()"
    >
      <div
        class="relative w-full max-w-md overflow-hidden rounded-2xl bg-white p-8 text-center shadow-xl ring-1 ring-stone-200 dark:bg-neutral-800 dark:ring-neutral-700"
      >
        <ConfettiBurst
          v-if="reward.kind === 'quiz' || reward.kind === 'certificate' || reward.kind === 'course'"
        />
        <span
          class="pointer-events-none absolute left-1/2 top-10 size-40 -translate-x-1/2 rounded-full bg-accent-400/30 animate-reward-burst"
          aria-hidden="true"
        />
        <span
          class="pointer-events-none absolute left-1/4 top-16 size-16 rounded-full bg-brand-400/25 animate-reward-burst"
          aria-hidden="true"
        />
        <span
          class="pointer-events-none absolute right-8 top-20 size-12 rounded-full bg-accent-300/40 animate-reward-burst"
          aria-hidden="true"
        />

        <div class="relative animate-reward-pop">
          <p class="text-[11px] font-semibold uppercase tracking-wider text-accent-700 dark:text-accent-400">
            {{ t(KIND_LABEL[reward.kind]) }}
          </p>

          <img
            v-if="reward.kind !== 'lesson'"
            :src="trophyArt"
            alt=""
            width="176"
            height="176"
            class="mx-auto mt-1 size-44 object-contain drop-shadow-sm"
            aria-hidden="true"
          />
          <span
            v-else
            class="mx-auto mt-3 flex size-16 items-center justify-center rounded-full bg-accent-50 text-accent-700 dark:bg-accent-950 dark:text-accent-300"
            aria-hidden="true"
          >
            <AppIcon name="sparkles" size="xl" />
          </span>

          <h2
            :id="`reward-title-${reward.id}`"
            class="mt-4 text-xl font-semibold text-gray-900 dark:text-white"
          >
            {{ reward.title }}
          </h2>
          <p v-if="reward.body" class="mt-2 text-sm text-gray-600 dark:text-neutral-300">
            {{ reward.body }}
          </p>

          <p
            v-if="reward.score != null"
            class="mt-3 text-sm font-medium text-brand-700 dark:text-brand-300"
          >
            {{ t('rewards.score', { score: reward.score }) }}
          </p>

          <p
            v-if="reward.points"
            class="mt-4 inline-flex items-center gap-1.5 rounded-full bg-accent-50 px-3 py-1 text-sm font-semibold text-accent-800 animate-points-tick dark:bg-accent-950 dark:text-accent-200"
          >
            +{{ reward.points }} {{ t('common.points') }}
          </p>

          <div class="mt-6 flex flex-wrap justify-center gap-2">
            <BaseButton v-if="reward.ctaTo" :to="reward.ctaTo" icon="arrow-right" @click="rewards.dismiss()">
              {{ reward.ctaLabel ?? t('common.next') }}
            </BaseButton>
            <BaseButton :variant="reward.ctaTo ? 'secondary' : 'primary'" @click="rewards.dismiss()">
              {{ t('rewards.continue') }}
            </BaseButton>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
